import { ADMIN_API_ORIGIN } from '@/config/domains';
import type {
  ComputerRuntimeLogRangePreset,
  ComputerRuntimeLogRecall,
  ComputerRuntimeLogRecallDownload,
  ListComputerRuntimeLogRecallsReply,
  ListTuttiComputersReply,
  ListTuttiComputersRequest,
  TuttiComputer,
} from '@/types';
import api from './api';
import {
  RUNTIME_LOG_RANGE_WIRE_VALUE,
  runtimeLogRecallCollectionPath,
  runtimeLogRecallDownloadPath,
  runtimeLogRecallPath,
  TUTTI_COMPUTER_CONNECTION_WIRE_VALUE,
  tuttiComputerPath,
} from './tuttiComputerContract';
import {
  parseComputerRuntimeLogRecall,
  parseComputerRuntimeLogRecallDownload,
  parseComputerRuntimeLogRecallList,
  parseTuttiComputer,
  parseTuttiComputerList,
} from './tuttiComputerMapper';

const requestConfig = { baseURL: ADMIN_API_ORIGIN } as const;

export const tuttiComputerApi = {
  async list(
    request: ListTuttiComputersRequest,
  ): Promise<ListTuttiComputersReply> {
    const connectionStatus =
      request.connectionStatus === 'online' ||
      request.connectionStatus === 'offline'
        ? TUTTI_COMPUTER_CONNECTION_WIRE_VALUE[request.connectionStatus]
        : undefined;
    const response = await api.get(tuttiComputerPath(), {
      ...requestConfig,
      params: {
        nameQuery: request.nameQuery || undefined,
        ownerQuery: request.ownerQuery || undefined,
        connectionStatus,
        platform: request.platform || undefined,
        hostArtifactVersion: request.hostArtifactVersion || undefined,
        pageSize: request.pageSize,
        pageToken: request.pageToken || undefined,
      },
    });
    return parseTuttiComputerList(response);
  },

  async get(computerId: string): Promise<TuttiComputer> {
    const response = await api.get(
      tuttiComputerPath(computerId),
      requestConfig,
    );
    return parseTuttiComputer(response);
  },

  async listRuntimeLogRecalls(
    computerId: string,
    pageToken = '',
  ): Promise<ListComputerRuntimeLogRecallsReply> {
    const response = await api.get(runtimeLogRecallCollectionPath(computerId), {
      ...requestConfig,
      params: { pageSize: 20, pageToken: pageToken || undefined },
    });
    return parseComputerRuntimeLogRecallList(response);
  },

  async getRuntimeLogRecall(
    computerId: string,
    taskId: string,
  ): Promise<ComputerRuntimeLogRecall> {
    const response = await api.get(
      runtimeLogRecallPath(computerId, taskId),
      requestConfig,
    );
    return parseComputerRuntimeLogRecall(response);
  },

  async createRuntimeLogRecall(
    computerId: string,
    rangePreset: Exclude<ComputerRuntimeLogRangePreset, 'unknown'>,
  ): Promise<ComputerRuntimeLogRecall> {
    const response = await api.post(
      runtimeLogRecallCollectionPath(computerId),
      {
        computerId,
        rangePreset: RUNTIME_LOG_RANGE_WIRE_VALUE[rangePreset],
      },
      requestConfig,
    );
    return parseComputerRuntimeLogRecall(response);
  },

  async createRuntimeLogRecallDownload(
    computerId: string,
    taskId: string,
  ): Promise<ComputerRuntimeLogRecallDownload> {
    const response = await api.post(
      runtimeLogRecallDownloadPath(computerId, taskId),
      { computerId, taskId },
      requestConfig,
    );
    return parseComputerRuntimeLogRecallDownload(response);
  },
};

interface ApiErrorLike {
  message?: string;
  response?: {
    data?: {
      error?: string;
      errmsg?: string;
      message?: string;
      reason?: string;
    };
  };
}

export function tuttiComputerErrorMessage(error: unknown, fallback: string) {
  if (typeof error !== 'object' || error === null) {
    return fallback;
  }
  const apiError = error as ApiErrorLike;
  return (
    apiError.response?.data?.errmsg ||
    apiError.response?.data?.message ||
    apiError.response?.data?.reason ||
    apiError.response?.data?.error ||
    apiError.message ||
    fallback
  );
}
